import React, { useState, useMemo } from "react";

// ─── LICO Thresholds (Directive 11R2-2025, Appendix A) ───
const LICO = [0, 2666, 3318, 4080, 4953, 5618, 6336, 7054];
const getLICO = (size) => LICO[Math.min(size, 7)];

// ─── Provincial Exemptions Data (2025) ───
const PROVINCES = {
  AB: {
    name: "Alberta",
    statute: "Civil Enforcement Act",
    home: "$40,000 equity (prorated if co-owned)",
    clothing: "$4,000",
    furniture: "$4,000",
    vehicle: "$5,000",
    tools: "$10,000",
    medical: "Exempt (no limit)",
    rrsp: "Exempt (except last 12 months contributions)",
    other: "Farmers: up to 160 acres where principal residence is on farm, plus personal property needed for next 12 months of operations.",
  },
  BC: {
    name: "British Columbia",
    statute: "Court Order Enforcement Act",
    home: "$12,000 (Metro Vancouver/Capital Regional District), $9,000 elsewhere",
    clothing: "Exempt (no limit)",
    furniture: "$4,000",
    vehicle: "$5,000 ($2,000 if behind on child/spousal support)",
    tools: "$10,000",
    medical: "Exempt (no limit)",
    rrsp: "Exempt (except last 12 months contributions). RESP not exempt.",
    other: "",
  },
  MB: {
    name: "Manitoba",
    statute: "Executions Act / Judgments Act",
    home: "$2,500 sole owner; $1,500 co-owned",
    clothing: "Exempt",
    furniture: "$4,500",
    vehicle: "$3,000 (if needed for work)",
    tools: "$7,500",
    medical: "Exempt",
    rrsp: "Exempt (Registered Retirement Savings Protection Act)",
    other: "Food & fuel for 6 months (or cash equivalent). Farmers: machinery, animals, seed, up to 160 acres, farm dwelling/outbuildings.",
  },
  NB: {
    name: "New Brunswick",
    statute: "Provincial legislation",
    home: "Not exempt",
    clothing: "Exempt",
    furniture: "$5,000",
    vehicle: "$6,500 (if required for employment)",
    tools: "$6,500",
    medical: "Exempt",
    rrsp: "Exempt (except last 12 months contributions)",
    other: "Pets exempt. Farm property: 2 horses, 2 cows, 10 sheep, 2 hogs, 20 fowl + feed for 6 months; seed grain/potatoes (set quantities).",
  },
  NL: {
    name: "Newfoundland & Labrador",
    statute: "Provincial legislation",
    home: "$10,000 equity",
    clothing: "$4,000",
    furniture: "$4,000",
    vehicle: "$2,000",
    tools: "$10,000",
    medical: "Exempt",
    rrsp: "Exempt (except last 12 months contributions)",
    other: "Food & fuel for 12 months. Sentimental items up to $500. Fishing/farming/aquaculture personal property up to $10,000.",
  },
  NS: {
    name: "Nova Scotia",
    statute: "Judicature Act / Personal Property Security Act",
    home: "Not exempt",
    clothing: "Exempt",
    furniture: "$5,000",
    vehicle: "$6,500",
    tools: "$7,500 (incl. farm equipment/fishing nets). No limit on grain/seed or livestock for domestic family use.",
    medical: "Exempt",
    rrsp: "Exempt (except last 12 months contributions)",
    other: "Food & fuel exempt.",
  },
  ON: {
    name: "Ontario",
    statute: "Execution Act",
    home: "$10,783 equity",
    clothing: "Exempt (for you and dependants)",
    furniture: "$14,180 (as-is resale value)",
    vehicle: "$7,117 (net of any loan)",
    tools: "$14,405",
    medical: "Exempt",
    rrsp: "Exempt (except last 12 months contributions). Most pensions & certain life insurance protected. RDSP generally protected.",
    other: "",
  },
  PE: {
    name: "Prince Edward Island",
    statute: "Provincial legislation",
    home: "Not exempt",
    clothing: "Exempt",
    furniture: "$5,000 (incl. utensils, equipment, food & fuel)",
    vehicle: "$6,500 if used for work ($3,000 if not)",
    tools: "$2,000 (farming/fishing up to $5,000)",
    medical: "Exempt",
    rrsp: "Exempt (except last 12 months contributions)",
    other: "Farm property: livestock/fowl/machinery up to $5,000; seed for up to 100 acres.",
  },
  QC: {
    name: "Quebec",
    statute: "Code of Civil Procedure",
    home: "Subject to provincial rules",
    clothing: "Exempt",
    furniture: "$7,000 (market value assessed by bailiff)",
    vehicle: "Exempt if required for work (no fixed dollar cap)",
    tools: "Exempt if necessary for work",
    medical: "Exempt",
    rrsp: "Exempt (12-month contribution clawback applies)",
    other: "Food, fuel, linens exempt. Family papers, portraits, medals exempt. Pets exempt.",
  },
  SK: {
    name: "Saskatchewan",
    statute: "Enforcement of Money Judgements Act",
    home: "$50,000 equity ($100,000 jointly owned across spouses)",
    clothing: "$7,500 (including jewellery)",
    furniture: "Exempt (no set dollar limit)",
    vehicle: "$10,000 equity in one vehicle",
    tools: "Exempt (office furniture/equipment & more)",
    medical: "Exempt",
    rrsp: "Exempt (except last 12 months contributions)",
    other: "Farmers: cash/crops to next harvest; all livestock, machinery & equipment (incl. one car/truck) for up to 12 months; 2 bushels of seed per acre.",
  },
  NT: {
    name: "Northwest Territories",
    statute: "Exemptions Act",
    home: "Subject to territorial rules",
    clothing: "Exempt",
    furniture: "Exempt (basic household goods)",
    vehicle: "Exempt (one vehicle for work)",
    tools: "Exempt",
    medical: "Exempt",
    rrsp: "Exempt (except last 12 months contributions)",
    other: "Contact a Licensed Insolvency Trustee for current territorial limits.",
  },
  YT: {
    name: "Yukon",
    statute: "Exemptions Act",
    home: "Subject to territorial rules",
    clothing: "Exempt",
    furniture: "Exempt (basic household goods)",
    vehicle: "Exempt (one vehicle for work)",
    tools: "Exempt",
    medical: "Exempt",
    rrsp: "Exempt (except last 12 months contributions)",
    other: "Contact a Licensed Insolvency Trustee for current territorial limits.",
  },
  NU: {
    name: "Nunavut",
    statute: "Consolidation of Exemptions Act",
    home: "Subject to territorial rules",
    clothing: "Exempt",
    furniture: "Exempt (basic household goods)",
    vehicle: "Exempt (one vehicle for work)",
    tools: "Exempt",
    medical: "Exempt",
    rrsp: "Exempt (except last 12 months contributions)",
    other: "Contact a Licensed Insolvency Trustee for current territorial limits.",
  },
};

// ─── S.178 Debts That Survive ───
const DEBT_TYPES = [
  {
    id: "student_loan",
    label: "Student Loan (government)",
    survives: "conditional",
    explanation:
      "Survives if you filed bankruptcy less than 7 years after you stopped being a student. If it's been 7+ years since you left school, the debt is discharged. You can apply to court after 5 years if you can show continued financial hardship.",
    section: "s.178(1)(g)",
    hasDateCheck: true,
  },
  {
    id: "child_support",
    label: "Child Support",
    survives: "yes",
    explanation:
      "Child support obligations always survive bankruptcy. You must continue making payments. The stay of proceedings does not apply to family support claims.",
    section: "s.178(1)(b)",
  },
  {
    id: "spousal_support",
    label: "Spousal Support / Alimony",
    survives: "yes",
    explanation:
      "Spousal support obligations always survive bankruptcy. These debts cannot be discharged.",
    section: "s.178(1)(b)",
  },
  {
    id: "court_fines",
    label: "Court Fines or Penalties",
    survives: "yes",
    explanation:
      "Fines, penalties, or restitution orders imposed by a court for an offence are not discharged. This includes criminal fines and court-ordered restitution.",
    section: "s.178(1)(a)",
  },
  {
    id: "fraud_debt",
    label: "Debt Obtained by Fraud",
    survives: "yes",
    explanation:
      "If you obtained property or services through false pretences or misrepresentation, that debt survives. The creditor must prove fraud in court.",
    section: "s.178(1)(e)",
  },
  {
    id: "credit_cards",
    label: "Credit Card Debt",
    survives: "no",
    explanation:
      "Regular credit card debt is discharged in bankruptcy. However, if a creditor can prove you made purchases knowing you couldn't pay (especially large purchases shortly before filing), they may challenge this under the fraud provision.",
    section: "s.178(2)",
  },
  {
    id: "personal_loans",
    label: "Personal Loans (unsecured)",
    survives: "no",
    explanation:
      "Unsecured personal loans, lines of credit, and payday loans are discharged in bankruptcy.",
    section: "s.178(2)",
  },
  {
    id: "medical_bills",
    label: "Medical / Dental Bills",
    survives: "no",
    explanation:
      "Unpaid medical and dental bills are unsecured debts that are discharged in bankruptcy.",
    section: "s.178(2)",
  },
  {
    id: "income_tax",
    label: "Income Tax Debt (CRA)",
    survives: "no",
    explanation:
      "Most income tax debt is discharged in bankruptcy. However, CRA can sometimes oppose your discharge. Tax refunds for years up to and including the year you file become part of your estate.",
    section: "s.178(2)",
  },
  {
    id: "mortgage",
    label: "Mortgage",
    survives: "secured",
    explanation:
      "Mortgages are secured debts — they are NOT included in bankruptcy. If you keep your home and continue payments, the mortgage stays. If you stop paying, the lender can still enforce the security.",
    section: "Secured debt",
  },
  {
    id: "car_loan",
    label: "Car Loan",
    survives: "secured",
    explanation:
      "Car loans are secured by the vehicle. They are not included in bankruptcy. Keep making payments to keep the car. The lender cannot terminate the loan just because you filed.",
    section: "Secured debt",
  },
  {
    id: "utility_bills",
    label: "Utility Bills",
    survives: "no",
    explanation:
      "Outstanding utility bills from before your filing date are discharged. Utilities cannot cut off service solely because you filed bankruptcy.",
    section: "s.178(2)",
  },
  {
    id: "undisclosed",
    label: "Debt I Didn't Tell My Trustee About",
    survives: "conditional",
    explanation:
      "If you failed to disclose a debt to your trustee, that creditor can pursue you for the dividend they would have received — unless the creditor already knew about your bankruptcy and failed to file a claim.",
    section: "s.178(1)(f)",
  },
  {
    id: "fiduciary",
    label: "Debt from Breach of Trust / Fiduciary Duty",
    survives: "yes",
    explanation:
      "Debts arising from fraud, embezzlement, misappropriation, or defalcation while acting in a fiduciary capacity survive bankruptcy.",
    section: "s.178(1)(d)",
  },
];

// ─── Duties Checklist ───
const DUTIES = [
  {
    id: "disclose_assets",
    title: "Disclose all your assets",
    detail:
      "Tell your trustee about everything you own — bank accounts, investments, property, vehicles, valuables. Even things you think are worthless. Hiding assets is a criminal offence.",
    ref: "BIA s.158(a)(b)",
    category: "immediate",
  },
  {
    id: "surrender_assets",
    title: "Surrender non-exempt assets",
    detail:
      "Hand over any assets that are not protected by your province's exemptions. Your trustee will tell you what's exempt and what isn't.",
    ref: "BIA s.158(a)",
    category: "immediate",
  },
  {
    id: "surrender_cards",
    title: "Hand over all credit cards",
    detail:
      "Give every credit card to your trustee for cancellation. All of them — even store cards, gas cards, and cards with a zero balance.",
    ref: "BIA s.158(c)",
    category: "immediate",
  },
  {
    id: "statement_affairs",
    title: "Complete your Statement of Affairs",
    detail:
      "This sworn document lists all your debts, assets, income, and expenses. Your trustee will help you prepare it, but you are responsible for its accuracy.",
    ref: "BIA s.158(d)",
    category: "immediate",
  },
  {
    id: "tax_returns",
    title: "File outstanding tax returns",
    detail:
      "Your trustee will file a pre-bankruptcy tax return. Any refunds from years up to and including your filing year go to the estate. Provide all T4s and tax documents.",
    ref: "BIA s.158(h.1)",
    category: "immediate",
  },
  {
    id: "monthly_reports",
    title: "File monthly income & expense reports",
    detail:
      "Every month during your bankruptcy, report your household income to your trustee. Provide pay stubs or other proof. This is how surplus income is calculated.",
    ref: "BIA s.68, Directive 11R2",
    category: "ongoing",
  },
  {
    id: "surplus_payments",
    title: "Make surplus income payments",
    detail:
      "If your household income exceeds the government threshold for your family size, you must pay 50% of the surplus to your trustee each month.",
    ref: "BIA s.68, Directive 11R2",
    category: "ongoing",
  },
  {
    id: "cooperate",
    title: "Cooperate with your trustee",
    detail:
      "Respond to your trustee's requests for information. Provide documents when asked — tax returns, bank statements, pay stubs. Delays hurt your case.",
    ref: "BIA s.158",
    category: "ongoing",
  },
  {
    id: "report_changes",
    title: "Report any changes in your situation",
    detail:
      "New job, raise, job loss, inheritance, lottery win, change of address, change in household — tell your trustee immediately. Material changes affect your surplus income.",
    ref: "BIA s.68(3)(a)",
    category: "ongoing",
  },
  {
    id: "no_new_credit",
    title: "Disclose bankruptcy when getting credit over $1,000",
    detail:
      "While undischarged, you must tell anyone extending you credit over $1,000 that you are bankrupt. Failing to do so is an offence.",
    ref: "BIA s.199(a)(b)",
    category: "ongoing",
  },
  {
    id: "no_business_hide",
    title: "Disclose bankruptcy in business dealings",
    detail:
      "If you engage in trade or business, you must disclose to all persons you transact with that you are an undischarged bankrupt.",
    ref: "BIA s.199(a)",
    category: "ongoing",
  },
  {
    id: "counselling_1",
    title: "Attend financial counselling — Session 1",
    detail:
      "Your first counselling session must happen within 60 days of filing. It covers budgeting, money management, and the causes of your financial difficulty.",
    ref: "BIA s.157.1, Directive 1R7",
    category: "milestone",
    deadline: "Within 60 days of filing",
  },
  {
    id: "counselling_2",
    title: "Attend financial counselling — Session 2",
    detail:
      "Your second session must happen within 210 days of filing (7 months). It covers rebuilding credit and avoiding future financial problems.",
    ref: "BIA s.157.1, Directive 1R7",
    category: "milestone",
    deadline: "Within 210 days of filing",
  },
  {
    id: "attend_meeting",
    title: "Attend meeting of creditors (if called)",
    detail:
      "Creditors may request a meeting. If one is called, you must attend and answer questions under oath about your finances.",
    ref: "BIA s.158(e)",
    category: "if_required",
  },
  {
    id: "official_receiver",
    title: "Attend examination by Official Receiver (if required)",
    detail:
      "The Official Receiver (an officer of the OSB) may examine you under oath. If called, you must attend. This is separate from the creditor meeting.",
    ref: "BIA s.161",
    category: "if_required",
  },
];

// ─── Rights ───
const RIGHTS = [
  {
    id: "stay",
    title: "Creditors Must Stop Collecting",
    icon: "🛡️",
    detail:
      "The moment you file bankruptcy, an automatic stay of proceedings takes effect. Creditors cannot call you, sue you, garnish your wages, or take any collection action. This protection starts immediately.",
    ref: "BIA s.69",
  },
  {
    id: "choose_lit",
    title: "You Choose Your Trustee",
    icon: "🤝",
    detail:
      "You have the right to choose which Licensed Insolvency Trustee handles your case. You are not limited to whoever a creditor, lawyer, or advisor suggests. Shop around. Get a free assessment.",
    ref: "Professional practice",
  },
  {
    id: "free_assessment",
    title: "Free Initial Assessment",
    icon: "💬",
    detail:
      "Every Licensed Insolvency Trustee must provide a free initial assessment explaining all your options — not just bankruptcy. This includes consumer proposals, debt consolidation, and other alternatives. You are never obligated to file.",
    ref: "Directive 6R5",
  },
  {
    id: "options_explained",
    title: "All Options Must Be Explained",
    icon: "📋",
    detail:
      "Your trustee must explain all available options before you sign anything. If they push you toward bankruptcy without discussing alternatives, that's a red flag.",
    ref: "Directive 6R5, BIA s.50.4",
  },
  {
    id: "keep_exempt",
    title: "You Keep Exempt Assets",
    icon: "🏠",
    detail:
      "You do not lose everything. Provincial laws protect essential assets — clothing, furniture, tools of your trade, a vehicle (up to a limit), and sometimes home equity. See the Provincial Exemptions tool for your province's limits.",
    ref: "BIA s.67, Provincial legislation",
  },
  {
    id: "keep_rrsp",
    title: "RRSPs Are Protected",
    icon: "💰",
    detail:
      "Registered Retirement Savings Plans (RRSPs) and RRIFs are protected across Canada — except for contributions made in the 12 months before you filed. Your retirement savings are generally safe.",
    ref: "BIA s.67(1)(b.3)",
  },
  {
    id: "fresh_start",
    title: "You Get a Fresh Start",
    icon: "🌅",
    detail:
      "Bankruptcy exists because Canada recognizes that honest people can get into financial trouble. The law is designed to help you recover, not to punish you. Most debts are eliminated and you rebuild from there.",
    ref: "BIA Preamble",
  },
  {
    id: "mediation",
    title: "Right to Mediation on Surplus Income",
    icon: "⚖️",
    detail:
      "If you disagree with the surplus income amount your trustee calculated, you have the right to request mediation through the OSB. An independent mediator will review your case.",
    ref: "BIA s.68(5)(6)",
  },
  {
    id: "discharge",
    title: "Right to Be Discharged",
    icon: "✅",
    detail:
      "If you complete all your duties, a first-time bankrupt with no surplus income is automatically discharged after 9 months. No one can block your discharge without going to court and providing reasons.",
    ref: "BIA s.168.1",
  },
  {
    id: "complain",
    title: "Right to Complain About Your Trustee",
    icon: "📞",
    detail:
      "If you believe your trustee is not acting properly, you can file a complaint with the Office of the Superintendent of Bankruptcy. Trustees are regulated professionals with a duty to act fairly.",
    ref: "BIA s.14.01, Directive 13R8",
  },
];

// ─── Styles ───
const C = {
  bg: "#f7f6f3",
  card: "#ffffff",
  text: "#2d2a26",
  textMid: "#55504a",
  textLight: "#8a8480",
  accent: "#1a6847",
  accentLight: "#e8f5ee",
  accentDark: "#0f4a31",
  warm: "#c4956a",
  warmLight: "#fdf5ed",
  warmDark: "#8b6340",
  border: "#e8e4df",
  danger: "#b44c3a",
  dangerLight: "#fdf0ed",
  warning: "#a07800",
  warningLight: "#fff9e6",
  info: "#3a6fb4",
  infoLight: "#edf4fd",
  purple: "#6b4fa0",
  purpleLight: "#f3eff9",
};

// ─── TABS ───
const TABS = [
  { id: "timeline", label: "Timeline", emoji: "📅" },
  { id: "surplus", label: "Surplus Income", emoji: "💰" },
  { id: "duties", label: "Your Duties", emoji: "✓" },
  { id: "rights", label: "Your Rights", emoji: "🛡️" },
  { id: "debts", label: "Debts", emoji: "📄" },
  { id: "exemptions", label: "What You Keep", emoji: "🏠" },
];

// ────────────────────────────────────────────
// COMPONENT: Timeline Navigator
// ────────────────────────────────────────────
function TimelineNavigator() {
  const [filingDate, setFilingDate] = useState("");
  const [isSecond, setIsSecond] = useState(false);
  const [hasSurplus, setHasSurplus] = useState(false);

  const milestones = useMemo(() => {
    if (!filingDate) return [];
    const fd = new Date(filingDate + "T00:00:00");
    if (isNaN(fd.getTime())) return [];
    const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
    const addMonths = (d, n) => { const r = new Date(d); r.setMonth(r.getMonth() + n); return r; };
    const dur = isSecond ? (hasSurplus ? 36 : 24) : (hasSurplus ? 21 : 9);
    const items = [
      { date: fd, title: "Filing Date", desc: "Bankruptcy filed. Stay of proceedings begins. Creditors must stop all collection.", icon: "📝" },
      { date: fd, title: "Surrender Credit Cards", desc: "Hand all credit cards to your trustee immediately.", icon: "💳" },
      { date: addDays(fd, 60), title: "Counselling Session 1 — Deadline", desc: "First financial counselling session. Covers budgeting and causes of financial difficulty.", icon: "🎓" },
      { date: addDays(fd, 210), title: "Counselling Session 2 — Deadline", desc: "Second counselling session. Covers rebuilding credit and avoiding future problems.", icon: "🎓" },
    ];
    if (hasSurplus) {
      items.push({ date: addMonths(fd, isSecond ? 24 : 9), title: `Month ${isSecond ? 24 : 9} — Surplus Review`, desc: "If total surplus income exceeds $200/month average, bankruptcy is extended.", icon: "📊" });
    }
    items.push({ date: addMonths(fd, dur), title: "Automatic Discharge Date", desc: `If all duties completed and no opposition filed, automatic discharge after ${dur} months.`, icon: "🌅" });
    return items.sort((a, b) => a.date - b.date);
  }, [filingDate, isSecond, hasSurplus]);

  const today = new Date();
  const dur = isSecond ? (hasSurplus ? 36 : 24) : (hasSurplus ? 21 : 9);
  const fd = filingDate ? new Date(filingDate + "T00:00:00") : null;
  const dd = fd ? (() => { const r = new Date(fd); r.setMonth(r.getMonth() + dur); return r; })() : null;
  const totalD = fd && dd ? (dd - fd) / 864e5 : 0;
  const elapsed = fd && totalD > 0 ? Math.max(0, Math.min(totalD, (today - fd) / 864e5)) : 0;
  const pct = totalD > 0 ? Math.round((elapsed / totalD) * 100) : 0;
  const fmtD = (d) => d.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });

  return (
    <div>
      <div style={cardS}>
        <h2 style={titleS}>Bankruptcy Timeline</h2>
        <p style={descS}>Enter your filing date to see every milestone and deadline — counselling sessions, surplus review, and discharge date.</p>
        <div style={rowS}>
          <div>
            <label style={labelS}>Filing Date</label>
            <input type="date" style={inputS} value={filingDate} onChange={(e) => setFilingDate(e.target.value)} />
          </div>
          <div>
            <label style={labelS}>Which bankruptcy?</label>
            <select style={selectS} value={isSecond ? "second" : "first"} onChange={(e) => setIsSecond(e.target.value === "second")}>
              <option value="first">First-time</option>
              <option value="second">Second (or subsequent)</option>
            </select>
          </div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 14, marginBottom: 8 }}>
          <input type="checkbox" checked={hasSurplus} onChange={(e) => setHasSurplus(e.target.checked)} style={{ width: 18, height: 18 }} />
          <span><strong>Surplus income applies</strong> — income exceeds LICO threshold by more than $200/month</span>
        </label>
        {filingDate && fd && dd && (
          <div style={{ background: C.warmLight, borderRadius: 10, padding: "16px 20px", marginTop: 12 }}>
            <div style={{ fontSize: 13, color: C.warmDark, fontWeight: 600, marginBottom: 8 }}>
              Duration: {dur} months ({isSecond ? (hasSurplus ? "2nd + surplus" : "2nd time") : hasSurplus ? "1st + surplus" : "1st time"})
            </div>
            <div style={{ height: 8, borderRadius: 4, background: C.border, overflow: "hidden", marginBottom: 8 }}>
              <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg, ${C.accent}, ${C.warm})`, borderRadius: 4, transition: "width 0.5s" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.textLight }}>
              <span>Filed: {fmtD(fd)}</span>
              <span style={{ fontWeight: 700, color: pct >= 100 ? C.accent : C.warm }}>{pct >= 100 ? "✅ Discharge eligible" : `${pct}% complete`}</span>
              <span>Discharge: {fmtD(dd)}</span>
            </div>
          </div>
        )}
      </div>
      {milestones.length > 0 && (
        <div style={cardS}>
          <h3 style={{ ...titleS, fontSize: 17 }}>Your Milestones</h3>
          {milestones.map((m, i) => {
            const past = m.date < today;
            const near = !past && (m.date - today) < 14 * 864e5;
            return (
              <div key={i} style={{ display: "flex", gap: 16, padding: 16, borderRadius: 10, background: near ? C.warmLight : past ? C.accentLight : "white", border: `1px solid ${near ? C.warm : past ? C.accent : C.border}`, marginBottom: 10 }}>
                <div style={{ width: 32, height: 32, minWidth: 32, borderRadius: "50%", background: near ? C.warm : past ? C.accent : C.border, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>
                  {past ? "✓" : m.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
                    <strong style={{ fontSize: 15 }}>{m.title}</strong>
                    <span style={{ fontSize: 13, color: past ? C.accent : near ? C.danger : C.textLight, fontWeight: past || near ? 600 : 400, fontFamily: "'IBM Plex Mono', monospace" }}>{fmtD(m.date)}</span>
                  </div>
                  <p style={{ fontSize: 14, color: C.textLight, margin: 0, lineHeight: 1.5 }}>{m.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────
// COMPONENT: Surplus Income Calculator
// ────────────────────────────────────────────
function SurplusCalculator() {
  const [familySize, setFamilySize] = useState(1);
  const [income, setIncome] = useState("");
  const [spouseIncome, setSpouseIncome] = useState("");
  const [deductions, setDeductions] = useState("");
  const threshold = getLICO(familySize);
  const total = (parseFloat(income) || 0) + (parseFloat(spouseIncome) || 0);
  const ded = parseFloat(deductions) || 0;
  const avail = total - ded;
  const surplus = avail - threshold;
  const hasSurp = surplus > 200;
  const payment = hasSurp ? Math.round(surplus * 50) / 100 : 0;
  const fmt = (n) => "$" + Math.abs(n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div>
      <div style={cardS}>
        <h2 style={titleS}>Surplus Income Calculator</h2>
        <p style={descS}>If your household income is higher than what the government says your family needs to live, you pay 50% of the difference. This is the part of bankruptcy most people don't understand until they get the bill.</p>
        <div style={rowS}>
          <div>
            <label style={labelS}>Family size (people in household)</label>
            <select style={selectS} value={familySize} onChange={(e) => setFamilySize(parseInt(e.target.value))}>
              {[1, 2, 3, 4, 5, 6, 7].map((n) => (<option key={n} value={n}>{n} {n === 7 ? "or more" : n === 1 ? "person" : "persons"}</option>))}
            </select>
          </div>
          <div>
            <label style={labelS}>Government threshold (LICO)</label>
            <div style={{ padding: "12px 14px", borderRadius: 8, background: C.accentLight, border: `1px solid ${C.accent}40`, fontSize: 18, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: C.accent }}>
              {fmt(threshold)}<span style={{ fontSize: 12, fontWeight: 400, color: C.textLight, display: "block" }}>per month</span>
            </div>
          </div>
        </div>
        <div style={rowS}>
          <div><label style={labelS}>Your net monthly income ($)</label><input type="number" style={inputS} value={income} onChange={(e) => setIncome(e.target.value)} placeholder="0.00" /></div>
          <div><label style={labelS}>Spouse's net monthly income ($)</label><input type="number" style={inputS} value={spouseIncome} onChange={(e) => setSpouseIncome(e.target.value)} placeholder="0.00" /></div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={labelS}>Non-discretionary deductions ($) <span style={{ fontWeight: 400, color: C.textLight }}> — child support, medical, child care</span></label>
          <input type="number" style={{ ...inputS, maxWidth: 300 }} value={deductions} onChange={(e) => setDeductions(e.target.value)} placeholder="0.00" />
        </div>
      </div>
      {(income || spouseIncome) && (
        <div style={cardS}>
          <h3 style={{ ...titleS, fontSize: 17 }}>Your Calculation</h3>
          {[["Total household income", fmt(total)], ["Less: Non-discretionary deductions", `(${fmt(ded)})`], ["Available monthly income", fmt(avail)], ["Less: Government threshold (LICO)", `(${fmt(threshold)})`]].map(([l, v], i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: i === 3 ? `2px solid ${C.text}` : `1px solid ${C.border}`, fontSize: 15 }}>
              <span>{l}</span><span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: i === 2 ? 700 : 400 }}>{v}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0", fontSize: 18, fontWeight: 700 }}>
            <span>{surplus >= 0 ? "Monthly Surplus" : "Monthly Deficit"}</span>
            <span style={{ fontFamily: "'IBM Plex Mono', monospace", color: surplus > 200 ? C.danger : C.accent }}>{surplus >= 0 ? fmt(surplus) : `(${fmt(surplus)})`}</span>
          </div>
          <div style={{ background: hasSurp ? C.dangerLight : C.accentLight, borderLeft: `4px solid ${hasSurp ? C.danger : C.accent}`, borderRadius: "0 8px 8px 0", padding: "20px 24px", marginTop: 12 }}>
            {hasSurp ? (<>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.danger, marginBottom: 8 }}>Surplus income payment required</div>
              <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6 }}>Your available income exceeds the threshold by more than $200. You must pay <strong>50% of the surplus = {fmt(payment)}/month</strong> to your trustee.</div>
              <div style={{ marginTop: 12, padding: "12px 16px", background: "rgba(180,76,58,0.08)", borderRadius: 8, fontSize: 14, color: C.danger, fontWeight: 600 }}>⏱ This extends your bankruptcy — first-time: 9→21 months. Second-time: 24→36 months.</div>
            </>) : surplus > 0 ? (<>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 8 }}>No surplus payment</div>
              <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6 }}>Your income exceeds the threshold by {fmt(surplus)}, but since that's under $200, no payment is required.</div>
            </>) : (<>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.accent, marginBottom: 8 }}>No surplus income</div>
              <div style={{ fontSize: 14, color: C.text, lineHeight: 1.6 }}>Your available income is below the government threshold. No surplus payment required.</div>
            </>)}
          </div>
          <div style={{ marginTop: 16, fontSize: 12, color: C.textLight, lineHeight: 1.5 }}>Source: Directive No. 11R2-2025, Appendix A — effective March 31, 2025.</div>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────
// COMPONENT: Duties Checklist
// ────────────────────────────────────────────
function DutiesChecklist() {
  const [checked, setChecked] = useState({});
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setChecked((p) => ({ ...p, [id]: !p[id] }));
  const categories = [
    { key: "immediate", label: "Do Immediately (at filing)" },
    { key: "ongoing", label: "Ongoing (every month)" },
    { key: "milestone", label: "Key Milestones" },
    { key: "if_required", label: "If Required" },
  ];
  const done = Object.values(checked).filter(Boolean).length;

  return (
    <div>
      <div style={cardS}>
        <h2 style={titleS}>Your Duties in Bankruptcy</h2>
        <p style={descS}>Legal obligations under the BIA. Complete all of these for your discharge. Failure can result in opposition, extension, or criminal charges.</p>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 20 }}>
          <div style={{ textAlign: "center", padding: "16px 24px", borderRadius: 10, background: C.accentLight, border: `1px solid ${C.accent}30`, flex: 1, minWidth: 100 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: C.accent, fontFamily: "'IBM Plex Mono', monospace" }}>{done}</div>
            <div style={{ fontSize: 12, color: C.textLight, marginTop: 4 }}>Completed</div>
          </div>
          <div style={{ textAlign: "center", padding: "16px 24px", borderRadius: 10, background: C.warmLight, border: `1px solid ${C.warm}30`, flex: 1, minWidth: 100 }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: C.warm, fontFamily: "'IBM Plex Mono', monospace" }}>{DUTIES.length - done}</div>
            <div style={{ fontSize: 12, color: C.textLight, marginTop: 4 }}>Remaining</div>
          </div>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: C.border, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${Math.round((done / DUTIES.length) * 100)}%`, background: `linear-gradient(90deg, ${C.accent}, ${C.warm})`, borderRadius: 4, transition: "width 0.5s" }} />
        </div>
      </div>
      {categories.map((cat) => {
        const items = DUTIES.filter((d) => d.category === cat.key);
        if (!items.length) return null;
        return (
          <div key={cat.key}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.08em", margin: "28px 0 12px", paddingBottom: 8, borderBottom: `1px solid ${C.border}` }}>{cat.label}</div>
            {items.map((duty) => (
              <div key={duty.id} style={{ display: "flex", gap: 14, padding: 16, borderRadius: 10, border: `1px solid ${checked[duty.id] ? C.accent : C.border}`, background: checked[duty.id] ? C.accentLight : "white", cursor: "pointer", marginBottom: 10, alignItems: "flex-start" }} onClick={() => toggle(duty.id)}>
                <div style={{ width: 22, height: 22, minWidth: 22, borderRadius: 6, border: `2px solid ${checked[duty.id] ? C.accent : C.border}`, background: checked[duty.id] ? C.accent : "white", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 14, fontWeight: 700, marginTop: 2 }}>
                  {checked[duty.id] ? "✓" : ""}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <strong style={{ fontSize: 15, textDecoration: checked[duty.id] ? "line-through" : "none", opacity: checked[duty.id] ? 0.6 : 1 }}>{duty.title}</strong>
                    <button onClick={(e) => { e.stopPropagation(); setExpanded((p) => ({ ...p, [duty.id]: !p[duty.id] })); }} style={{ background: "none", border: "none", color: C.textLight, cursor: "pointer", fontSize: 18, padding: "0 4px" }}>{expanded[duty.id] ? "−" : "+"}</button>
                  </div>
                  {duty.deadline && <div style={{ fontSize: 12, color: C.warm, fontWeight: 600, marginTop: 4 }}>⏱ {duty.deadline}</div>}
                  {expanded[duty.id] && (
                    <div style={{ marginTop: 10, fontSize: 14, color: C.textLight, lineHeight: 1.6, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                      {duty.detail}
                      <div style={{ marginTop: 8, fontSize: 12, color: C.purple, fontFamily: "'IBM Plex Mono', monospace" }}>{duty.ref}</div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────
// COMPONENT: Know Your Rights
// ────────────────────────────────────────────
function KnowYourRights() {
  const [expanded, setExpanded] = useState({});
  return (
    <div>
      <div style={cardS}><h2 style={titleS}>Know Your Rights</h2><p style={descS}>Bankruptcy isn't just obligations — you have significant legal protections. The law is designed to give honest people a fresh start, not to punish you.</p></div>
      {RIGHTS.map((r) => (
        <div key={r.id} style={{ padding: 20, borderRadius: 10, border: `1px solid ${expanded[r.id] ? C.accent : C.border}`, background: "white", marginBottom: 12, cursor: "pointer", transition: "all 0.2s" }} onClick={() => setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 24 }}>{r.icon}</span>
            <strong style={{ flex: 1, fontSize: 15 }}>{r.title}</strong>
            <span style={{ color: C.textLight, fontSize: 18, transition: "transform 0.2s", transform: expanded[r.id] ? "rotate(180deg)" : "none" }}>▾</span>
          </div>
          {expanded[r.id] && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}`, fontSize: 14, color: C.textLight, lineHeight: 1.7 }}>
              {r.detail}
              <div style={{ marginTop: 10, fontSize: 12, color: C.purple, fontFamily: "'IBM Plex Mono', monospace" }}>{r.ref}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────
// COMPONENT: Debts That Survive
// ────────────────────────────────────────────
function DebtsSurvive() {
  const [expanded, setExpanded] = useState({});
  const [studentDate, setStudentDate] = useState("");
  const statusLabel = {
    yes: { text: "SURVIVES", color: C.danger, bg: C.dangerLight },
    no: { text: "DISCHARGED", color: C.accent, bg: C.accentLight },
    conditional: { text: "IT DEPENDS", color: C.warning, bg: C.warningLight },
    secured: { text: "SECURED DEBT", color: C.info, bg: C.infoLight },
  };

  return (
    <div>
      <div style={cardS}>
        <h2 style={titleS}>Which Debts Survive Bankruptcy?</h2>
        <p style={descS}>Most debts are eliminated when you're discharged. But some survive by law. Click any debt type to understand what happens to it.</p>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {[["Discharged", C.accent, DEBT_TYPES.filter((d) => d.survives === "no").length], ["Survive", C.danger, DEBT_TYPES.filter((d) => d.survives === "yes").length], ["Conditional", C.warning, DEBT_TYPES.filter((d) => d.survives === "conditional" || d.survives === "secured").length]].map(([l, c, n], i) => (
            <div key={i} style={{ textAlign: "center", padding: "14px 20px", borderRadius: 10, background: c + "15", border: `1px solid ${c}30`, flex: 1, minWidth: 80 }}>
              <div style={{ fontSize: 24, fontWeight: 700, color: c, fontFamily: "'IBM Plex Mono', monospace" }}>{n}</div>
              <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
      {DEBT_TYPES.map((debt) => {
        const s = statusLabel[debt.survives];
        const colors = { yes: C.danger, no: C.accent, conditional: C.warning, secured: C.info };
        return (
          <div key={debt.id} style={{ padding: "16px 20px", borderRadius: 10, borderLeft: `4px solid ${colors[debt.survives]}`, background: s.bg, marginBottom: 10, cursor: "pointer" }} onClick={() => setExpanded((p) => ({ ...p, [debt.id]: !p[debt.id] }))}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong style={{ fontSize: 15 }}>{debt.label}</strong>
              <span style={{ display: "inline-block", padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 700, color: s.color, background: "rgba(255,255,255,0.7)", letterSpacing: "0.03em", textTransform: "uppercase" }}>{s.text}</span>
            </div>
            {expanded[debt.id] && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                <p style={{ fontSize: 14, color: C.textMid, lineHeight: 1.7, margin: "0 0 8px" }}>{debt.explanation}</p>
                {debt.hasDateCheck && (
                  <div style={{ background: C.warmLight, borderRadius: 8, padding: 14, marginTop: 10 }}>
                    <label style={{ ...labelS, fontSize: 13 }}>When did you stop being a student?</label>
                    <input type="date" style={{ ...inputS, maxWidth: 200 }} value={studentDate} onChange={(e) => setStudentDate(e.target.value)} onClick={(e) => e.stopPropagation()} />
                    {studentDate && (() => {
                      const years = (new Date() - new Date(studentDate)) / (365.25 * 864e5);
                      const color = years >= 7 ? C.accent : years >= 5 ? C.warning : C.danger;
                      const msg = years >= 7 ? "✅ 7+ years — student loan would be discharged." : years >= 5 ? `⚠️ ${Math.floor(years)} years. Survives, but you may apply to court for hardship relief (available after 5 years).` : `❌ ${Math.floor(years)} years — student loan survives. Need 7 years since leaving school.`;
                      return <div style={{ marginTop: 10, fontSize: 14, fontWeight: 600, color }}>{msg}</div>;
                    })()}
                  </div>
                )}
                <div style={{ marginTop: 8, fontSize: 12, color: C.purple, fontFamily: "'IBM Plex Mono', monospace" }}>{debt.section}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ────────────────────────────────────────────
// COMPONENT: Provincial Exemptions
// ────────────────────────────────────────────
function ProvincialExemptions() {
  const [province, setProvince] = useState("");
  const data = province ? PROVINCES[province] : null;
  const fields = [
    { key: "home", label: "🏠 Home Equity" },
    { key: "clothing", label: "👕 Clothing" },
    { key: "furniture", label: "🛋️ Furnishings" },
    { key: "vehicle", label: "🚗 Vehicle" },
    { key: "tools", label: "🔧 Tools of Trade" },
    { key: "medical", label: "⚕️ Medical Aids" },
    { key: "rrsp", label: "💰 RRSP / Pensions" },
  ];

  return (
    <div>
      <div style={cardS}>
        <h2 style={titleS}>What You Keep — Provincial Exemptions</h2>
        <p style={descS}>You do not lose everything. Every province protects essential assets. The limits depend on where you live.</p>
        <div style={{ marginBottom: 16 }}>
          <label style={labelS}>Select your province or territory</label>
          <select style={selectS} value={province} onChange={(e) => setProvince(e.target.value)}>
            <option value="">— Choose —</option>
            {Object.entries(PROVINCES).map(([code, p]) => (<option key={code} value={code}>{p.name}</option>))}
          </select>
        </div>
      </div>
      {data ? (
        <div style={cardS}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ ...titleS, fontSize: 18, margin: 0 }}>{data.name} — 2025</h3>
            <span style={{ display: "inline-block", padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700, color: C.purple, background: C.purpleLight, letterSpacing: "0.03em", textTransform: "uppercase" }}>{data.statute}</span>
          </div>
          {fields.map((f) => (
            <div key={f.key} style={{ display: "flex", justifyContent: "space-between", padding: "14px 0", borderBottom: `1px solid ${C.border}`, alignItems: "flex-start", gap: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text, minWidth: 140 }}>{f.label}</div>
              <div style={{ fontSize: 14, color: C.textLight, textAlign: "right", flex: 1 }}>{data[f.key]}</div>
            </div>
          ))}
          {data.other && (
            <div style={{ marginTop: 16, padding: "14px 18px", background: C.warmLight, borderRadius: 8, fontSize: 14, color: C.warmDark, lineHeight: 1.6 }}>
              <strong>Additional notes:</strong> {data.other}
            </div>
          )}
          <div style={{ marginTop: 20, padding: "14px 18px", background: C.infoLight, borderRadius: 8, fontSize: 13, color: C.info, lineHeight: 1.6 }}>
            <strong>Important:</strong> Exemptions apply to <em>equity</em> (value minus what you owe). RRSP contributions in the 12 months before filing are not protected. Contact a Licensed Insolvency Trustee to confirm current amounts.
          </div>
        </div>
      ) : (
        <div style={cardS}>
          <h3 style={{ ...titleS, fontSize: 17 }}>Universally protected across Canada</h3>
          <p style={{ fontSize: 14, color: C.textLight, lineHeight: 1.7, margin: "0 0 16px" }}>Under federal law (BIA s.67), these are always exempt:</p>
          {["Property held in trust for other people", "GST/HST credits and prescribed payments for essential needs", "RRSPs and RRIFs (except contributions in the last 12 months)", "Necessary clothing for you and your dependants"].map((item, i) => (
            <div key={i} style={{ display: "flex", gap: 10, marginBottom: 10, fontSize: 14 }}>
              <span style={{ color: C.accent, fontWeight: 700 }}>✓</span><span>{item}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Shared styles ───
const cardS = { background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, padding: 28, marginBottom: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" };
const titleS = { fontSize: 20, fontWeight: 700, margin: "0 0 8px", color: C.text };
const descS = { fontSize: 15, color: C.textLight, margin: "0 0 20px", lineHeight: 1.6 };
const labelS = { display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6, color: C.text };
const inputS = { width: "100%", padding: "12px 14px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 15, fontFamily: "inherit", boxSizing: "border-box", outline: "none" };
const selectS = { width: "100%", padding: "12px 14px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 15, fontFamily: "inherit", boxSizing: "border-box", background: "white", cursor: "pointer" };
const rowS = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 };

// ────────────────────────────────────────────
// MAIN APP
// ────────────────────────────────────────────
export default function BankruptSection() {
  const [activeTab, setActiveTab] = useState("timeline");
  const renderTab = () => {
    switch (activeTab) {
      case "timeline": return <TimelineNavigator />;
      case "surplus": return <SurplusCalculator />;
      case "duties": return <DutiesChecklist />;
      case "rights": return <KnowYourRights />;
      case "debts": return <DebtsSurvive />;
      case "exemptions": return <ProvincialExemptions />;
      default: return <TimelineNavigator />;
    }
  };

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: C.bg, minHeight: "100vh", color: C.text }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;600;700&display=swap" rel="stylesheet" />
      <header style={{ background: "linear-gradient(135deg, #1a6847 0%, #0f4a31 100%)", color: "white" }}>
        <div style={{ background: "#111", padding: "8px 24px", fontSize: 13, display: "flex", alignItems: "center", gap: 8, letterSpacing: "0.03em" }}>
          <span style={{ color: "#aaa" }}>navigationalmind.com</span>
          <span style={{ color: "#555" }}>/</span>
          <span style={{ color: "#aaa" }}>nma-bankruptcy</span>
          <span style={{ color: "#555" }}>/</span>
          <span style={{ color: "#ddd" }}>bankrupt</span>
          <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 12, background: "rgba(255,255,255,0.15)", fontSize: 11, fontWeight: 600, marginLeft: 12, letterSpacing: "0.04em" }}>v1.0</span>
        </div>
        <div style={{ padding: "48px 24px 32px", maxWidth: 900, margin: "0 auto" }}>
          <h1 style={{ fontSize: "clamp(28px, 5vw, 42px)", fontWeight: 700, margin: "0 0 12px", lineHeight: 1.15, letterSpacing: "-0.02em" }}>
            You Filed Bankruptcy.<br />Here's What Happens Now.
          </h1>
          <p style={{ fontSize: "clamp(15px, 2.5vw, 18px)", opacity: 0.85, margin: 0, lineHeight: 1.5, maxWidth: 650 }}>
            Plain-language tools to understand your timeline, obligations, rights, and what you keep. No legalese. No judgment. Just the truth about your situation.
          </p>
        </div>
        <div style={{ display: "flex", gap: 4, padding: "0 24px", overflowX: "auto", maxWidth: 960, margin: "0 auto" }}>
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              padding: "12px 16px", fontSize: 13, fontWeight: activeTab === tab.id ? 700 : 500,
              color: activeTab === tab.id ? "white" : "rgba(255,255,255,0.65)",
              background: activeTab === tab.id ? "rgba(255,255,255,0.15)" : "transparent",
              border: "none", borderBottom: activeTab === tab.id ? "3px solid #fff" : "3px solid transparent",
              cursor: "pointer", whiteSpace: "nowrap", borderRadius: "8px 8px 0 0", transition: "all 0.2s", fontFamily: "inherit",
            }}>
              <span style={{ marginRight: 6 }}>{tab.emoji}</span>{tab.label}
            </button>
          ))}
        </div>
      </header>
      <main style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px 80px" }}>{renderTab()}</main>
      <footer style={{ textAlign: "center", padding: "32px 24px", fontSize: 13, color: C.textLight, borderTop: `1px solid ${C.border}`, maxWidth: 900, margin: "0 auto" }}>
        <p><strong>NMA Bankruptcy</strong> — Navigational Mind Architecture</p>
        <p style={{ marginTop: 8 }}>This tool provides general information based on the Bankruptcy & Insolvency Act and Directive 11R2-2025. It is not legal advice. Consult a Licensed Insolvency Trustee for advice specific to your circumstances.</p>
        <p style={{ marginTop: 8, fontSize: 12 }}>LICO thresholds: Directive 11R2-2025, Appendix A (March 31, 2025) · Provincial exemptions: 2025 values · Debts that survive: BIA s.178(1)</p>
      </footer>
    </div>
  );
}
